import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ExternalLink, Megaphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BannerCard - renders a sponsored banner card in the directory grid.
 * Automatically tracks impressions (once per mount) and click-throughs.
 */
export const BannerCard = ({ banner, sponsoredLabel = 'Sponsorizzato', ctaLabel = 'Scopri' }) => {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    axios.post(`${API}/banners/${banner.id}/impression`).catch(() => {});
  }, [banner.id]);

  const handleClick = async () => {
    try {
      const res = await axios.post(`${API}/banners/${banner.id}/click`);
      const url = res?.data?.link_url || banner.link_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (_e) {
      if (banner.link_url) {
        window.open(banner.link_url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  return (
    <Card
      className="border-2 border-dashed border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 rounded-md hover:border-orange-400 hover:shadow-md transition-all overflow-hidden"
      data-testid={`banner-card-${banner.id}`}
    >
      <CardContent className="p-0">
        {/* Sponsored label */}
        <div className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border-b border-orange-200">
          <Megaphone className="h-3.5 w-3.5 text-orange-600" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-orange-700 font-semibold">
            {sponsoredLabel}
          </span>
        </div>

        {/* Image */}
        {banner.image_url && (
          <div className="w-full h-40 bg-slate-100 overflow-hidden">
            <img
              src={banner.image_url}
              alt={banner.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="p-5">
          <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">
            {banner.company}
          </p>
          <h3
            className="text-lg font-bold text-slate-900 mb-2 line-clamp-2"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {banner.title}
          </h3>
          <p className="text-sm text-slate-600 mb-4 line-clamp-3 leading-relaxed">
            {banner.description}
          </p>

          <Button
            onClick={handleClick}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-semibold"
            data-testid={`banner-cta-${banner.id}`}
          >
            {ctaLabel}
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BannerCard;
